import { Dictionary } from '@ngrx/entity';
import { hasPermission } from '@sb/shared/permission/util';
import { plainDateList, plainDateRange, toISODateString, type PlainDate } from '@shiftbase-com/ts-shared/utilities';
import countBy from 'lodash-es/countBy';
import keyBy from 'lodash-es/keyBy';
import keys from 'lodash-es/keys';
import set from 'lodash-es/set';
import { createSelector } from 'reselect';

import { getPermissionState } from '../../../reducers/auth/permission.helper';
import { defaultDayLog } from '../../../reducers/orm/daylog/daylog.helper';
import { DaylogModel } from '../../../reducers/orm/daylog/daylog.model';
import {
  getDaylogsForPeriodAndDepartment,
  getDaylogsForPeriodAndDepartments,
  getDaylogsForPeriodAndSelectedDepartments,
} from '../../../reducers/orm/daylog/daylog.service';
import { EmployeeModel } from '../../../reducers/orm/employee/employee.model';
import { getEmployeeEntities } from '../../../reducers/orm/employee/employee.selectors';
import { getDepartmentOptions } from '../../../reducers/orm/location/location.service';
import { TimesheetModel } from '../../../reducers/orm/timesheet/timesheet.model';
import {
  getTimesheetsForPeriod,
  getTimesheetsForPeriodAndDepartments,
} from '../../../reducers/orm/timesheet/timesheet.service';
import { getSelectedDepartmentIds } from '../../../reducers/selected-departments/selected-departments.service';

export type OpenCloseStatus = 'closed' | 'open' | 'pending' | 'disabled';

export interface OpenCloseData {
  date: PlainDate;
  departmentId: string;
  canEdit: boolean;
  pendingTimesheetCount: number;
  isClosed: boolean;
  status: OpenCloseStatus;
  saving: boolean;
}

interface OpenCloseDepDays {
  [departmentId: string]: {
    [day: string]: OpenCloseData;
  };
}

export interface OpenClosePeriod {
  [departmentId: string]: OpenClosePeriodData;
}

export interface OpenClosePeriodData {
  canClose: boolean;
  isClosed: boolean;
}

const daylogMap = (daylog: DaylogModel, pendingCount: number, canClose: boolean, canOpen: boolean): OpenCloseData => {
  let status: OpenCloseStatus;
  const today = toISODateString(new Date());

  if (daylog.date > today) {
    status = 'disabled';
  } else if (!!daylog.finished_timesheet) {
    status = 'closed';
  } else if (pendingCount > 0) {
    status = 'pending';
  } else {
    status = 'open';
  }

  return {
    date: daylog.date,
    departmentId: daylog.department_id,
    canEdit: daylog.finished_timesheet ? canOpen : canClose,
    pendingTimesheetCount: pendingCount,
    isClosed: daylog.finished_timesheet,
    saving: daylog.saving || false,
    status,
  };
};

const checkPendingTimesheets = (
  employees: Dictionary<EmployeeModel>,
  timesheet: TimesheetModel,
  departmentId?: string,
  date?: string,
): boolean => {
  if (timesheet.status !== 'Pending') {
    return false;
  }

  if (date && timesheet.date !== date) {
    return false;
  }

  if (departmentId && timesheet.department_id !== departmentId) {
    return false;
  }

  if (employees[timesheet.user_id] && timesheet.date >= employees[timesheet.user_id].enddate) {
    return false;
  }

  return true;
};

/**
 * Shared by the globally-scoped and the explicitly-scoped selectors below. Kept a plain function
 * rather than having one selector compose the other: a parameterised selector can only be fed the
 * department ids by re-invoking it inside another selector's projector, which rebuilds it on every
 * emission and throws away memoisation for the existing consumers.
 */
const deriveOpenCloseDepDays = (
  departmentIds: string[],
  daylogs: DaylogModel[],
  timesheets: TimesheetModel[],
  employees: Dictionary<EmployeeModel>,
  permissionState: ReturnType<typeof getPermissionState>,
  minDate: PlainDate,
  maxDate: PlainDate,
): OpenCloseDepDays => {
  //filter pending timesheets
  const pendingTimesheets = timesheets.filter((timesheet: TimesheetModel) =>
    checkPendingTimesheets(employees, timesheet),
  );

  //get count per department per day
  const pendingTimesheetCountPerDepDay = countBy(
    pendingTimesheets,
    (timesheet: TimesheetModel) => `${timesheet.department_id}|${timesheet.date}`,
  );

  //get daylogs per department per day
  const daylogsPerDepDay = keyBy(daylogs, (daylog: DaylogModel) => `${daylog.department_id}|${daylog.date}`);

  const days = plainDateList(plainDateRange(minDate, maxDate));
  const openCloseDays: OpenCloseDepDays = {};

  departmentIds.forEach((departmentId) => {
    const canClose = hasPermission(
      {
        departments: departmentId,
        permissions: 'Close timesheets',
      },
      permissionState,
    );

    const canOpen = hasPermission(
      {
        departments: departmentId,
        permissions: 'Open timesheets',
      },
      permissionState,
    );

    days.forEach((day) => {
      const depDayKey = `${departmentId}|${day}`;
      const pendingCount = pendingTimesheetCountPerDepDay[depDayKey] || 0;
      const dayLog = daylogsPerDepDay[depDayKey] || defaultDayLog(day, departmentId);
      set(openCloseDays, [departmentId, day], daylogMap(dayLog, pendingCount, canClose, canOpen));
    });
  });

  return openCloseDays;
};

const deriveCloseAll = (openCloseDepDays: OpenCloseDepDays): OpenClosePeriod => {
  const closeAll: OpenClosePeriod = {};

  keys(openCloseDepDays).forEach((key) => {
    const daysToBeChecked = Object.values(openCloseDepDays[key]).filter((day) => day.status !== 'disabled');
    const isClosed = daysToBeChecked.every((day) => day.isClosed);
    const canClose = daysToBeChecked.every((day) => day.canEdit);

    closeAll[key] = { canClose, isClosed };
  });

  return closeAll;
};

export const getOpenClosedForPeriod = (minDate: PlainDate, maxDate: PlainDate) =>
  createSelector(
    getSelectedDepartmentIds,
    getDaylogsForPeriodAndSelectedDepartments(minDate, maxDate),
    getTimesheetsForPeriod(minDate, maxDate),
    getEmployeeEntities,
    getPermissionState,
    (departmentIds, daylogs, timesheets, employees, permissionState): OpenCloseDepDays =>
      deriveOpenCloseDepDays(departmentIds, daylogs, timesheets, employees, permissionState, minDate, maxDate),
  );

/**
 * `getOpenClosedForPeriod` for an explicit set of departments. Every selector the globally-scoped
 * variant reads — daylogs, timesheets and the locations grouping — keys off
 * `getSelectedDepartmentIds`, so a caller that owns its own department scope (the payroll run)
 * cannot reuse it: a department outside the global selection silently contributes no days at all.
 */
export const getOpenClosedForPeriodAndDepartments = (departmentIds: string[], minDate: PlainDate, maxDate: PlainDate) =>
  createSelector(
    getDaylogsForPeriodAndDepartments(departmentIds, minDate, maxDate),
    getTimesheetsForPeriodAndDepartments(departmentIds, minDate, maxDate),
    getEmployeeEntities,
    getPermissionState,
    (daylogs, timesheets, employees, permissionState): OpenCloseDepDays =>
      deriveOpenCloseDepDays(departmentIds, daylogs, timesheets, employees, permissionState, minDate, maxDate),
  );

/** The grid's whole render input for an explicit set of departments — see `getOpenClosedForPeriodAndDepartments`. */
export const getDataForOpenCloseDaysForDepartments = (
  departmentIds: string[],
  minDate: PlainDate,
  maxDate: PlainDate,
) =>
  createSelector(
    getOpenClosedForPeriodAndDepartments(departmentIds, minDate, maxDate),
    getDepartmentOptions(departmentIds),
    (openCloseDepDays, locationsWithDepartments) => ({
      days: plainDateList(plainDateRange(minDate, maxDate)),
      openCloseDepDays,
      locationsWithDepartments,
      closeAll: deriveCloseAll(openCloseDepDays),
    }),
  );

export type OpenCloseDaysData = ReturnType<ReturnType<typeof getDataForOpenCloseDaysForDepartments>>;

export const getOpenCloseDaysForDepartmentOnDate = (departmentId: string, date: PlainDate) =>
  createSelector(
    getDaylogsForPeriodAndDepartment(departmentId, date, date),
    getTimesheetsForPeriod(date, date),
    getEmployeeEntities,
    getPermissionState,
    (daylogs, timesheets, employees, permissionState): OpenCloseData => {
      //filter pending timesheets
      const pendingCount = timesheets.filter((timesheet: TimesheetModel) =>
        checkPendingTimesheets(employees, timesheet, departmentId, date),
      ).length;

      const canClose = hasPermission(
        {
          departments: departmentId,
          permissions: 'Close timesheets',
        },
        permissionState,
      );

      const canOpen = hasPermission(
        {
          departments: departmentId,
          permissions: 'Open timesheets',
        },
        permissionState,
      );

      const dayLog = daylogs.length > 0 ? daylogs[0] : defaultDayLog(date, departmentId);

      return daylogMap(dayLog, pendingCount, canClose, canOpen);
    },
  );
